// TRE-2105 — geometry shared by the mobile (Skia) and web (canvas 2D) body-photo comparison.
// Everything is expressed in "card units": the exported image is CARD_WIDTH × CARD_HEIGHT and the
// on-screen preview is the same card scaled down, so the preview and the shared PNG are the same
// drawing. Per-photo adjustments are stored relative to the photo's frame (fractions, not pixels),
// which keeps a client's alignment intact when she switches between the slider and the
// side-by-side layout, whose frames have different sizes.

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1350;
/** Bottom strip with the "PRZED / PO" labels and the wordmark. */
export const LABEL_BAR_HEIGHT = 150;
/** Gutter between the two photos in the side-by-side layout. */
export const SIDE_BY_SIDE_GAP = 12;
export const PHOTO_AREA_HEIGHT = CARD_HEIGHT - LABEL_BAR_HEIGHT;

/** How close (in card units) a touch has to land to the slider divider to grab it. */
export const DIVIDER_HIT_SLOP = 56;
/** Below 1 the photo shrinks inside its frame (background shows) — needed when "before" was shot closer. */
export const MIN_SCALE = 0.5;
export const MAX_SCALE = 4;
/** Zoom step for the +/- buttons; pinch and wheel are continuous. */
export const ZOOM_STEP = 1.15;
/**
 * How far (fraction of the frame) a photo may float past the cover-fit position even when there
 * is no slack — two photos are rarely framed alike, so aligning a body often means letting a
 * strip of background show at the top or bottom.
 */
export const FREE_PAN_MARGIN = 0.35;
export const MIN_FACE_RADIUS = 0.04;
export const MAX_FACE_RADIUS = 0.25;

export type PhotoCompareMode = "slider" | "sideBySide";
export type PhotoCompareSlotKey = "before" | "after";

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Circle {
  cx: number;
  cy: number;
  r: number;
}

/** Pan / zoom of one photo inside its frame. Offsets are fractions of the frame size. */
export interface PhotoAdjustment {
  scale: number;
  offsetX: number;
  offsetY: number;
}

/**
 * The face cover is anchored to the photo, not the frame: `x`/`y` are fractions of the image
 * size and `r` a fraction of the image width, so it follows the face when the photo is panned
 * or zoomed.
 */
export interface FaceCover {
  x: number;
  y: number;
  r: number;
}

export interface PhotoCompareSlot {
  adjustment: PhotoAdjustment;
  face: FaceCover;
}

export interface PhotoCompareState {
  mode: PhotoCompareMode;
  /** Slider divider position as a fraction of the photo area width. */
  split: number;
  hideFaces: boolean;
  before: PhotoCompareSlot;
  after: PhotoCompareSlot;
}

export type HitTarget =
  | { kind: "divider" }
  | { kind: "face"; slot: PhotoCompareSlotKey }
  /** The small grip on the cover's edge — dragging it changes the radius. */
  | { kind: "faceHandle"; slot: PhotoCompareSlotKey }
  | { kind: "photo"; slot: PhotoCompareSlotKey }
  | null;

/** Visual radius of the resize grip (card units) and how far around it a touch still counts. */
export const FACE_HANDLE_RADIUS = 14;
export const FACE_HANDLE_HIT_SLOP = 44;

/** The resize grip sits on the cover's edge, bottom-right, where a thumb naturally lands. */
export const getFaceHandle = (circle: Circle): Point => ({
  x: circle.cx + circle.r * Math.SQRT1_2,
  y: circle.cy + circle.r * Math.SQRT1_2,
});

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const createDefaultSlot = (): PhotoCompareSlot => ({
  adjustment: { scale: 1, offsetX: 0, offsetY: 0 },
  // A head-and-shoulders photo has the face around the top fifth of the image.
  face: { x: 0.5, y: 0.12, r: 0.11 },
});

export const createDefaultState = (mode: PhotoCompareMode = "slider"): PhotoCompareState => ({
  mode,
  split: 0.5,
  hideFaces: false,
  before: createDefaultSlot(),
  after: createDefaultSlot(),
});

/** Frame of each photo for the given layout. In slider mode both photos share the whole area. */
export const getFrames = (mode: PhotoCompareMode): Record<PhotoCompareSlotKey, Rect> => {
  if (mode === "sideBySide") {
    const width = (CARD_WIDTH - SIDE_BY_SIDE_GAP) / 2;
    return {
      before: { x: 0, y: 0, width, height: PHOTO_AREA_HEIGHT },
      after: { x: width + SIDE_BY_SIDE_GAP, y: 0, width, height: PHOTO_AREA_HEIGHT },
    };
  }
  const full = { x: 0, y: 0, width: CARD_WIDTH, height: PHOTO_AREA_HEIGHT };
  return { before: full, after: full };
};

/** Where the photo is drawn: cover-fit into the frame, then zoomed and panned. */
export const getImageRect = (frame: Rect, image: Size, adjustment: PhotoAdjustment): Rect => {
  const cover = Math.max(frame.width / image.width, frame.height / image.height);
  const scale = cover * adjustment.scale;
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: frame.x + (frame.width - width) / 2 + adjustment.offsetX * frame.width,
    y: frame.y + (frame.height - height) / 2 + adjustment.offsetY * frame.height,
    width,
    height,
  };
};

export const getFaceCircle = (imageRect: Rect, face: FaceCover): Circle => ({
  cx: imageRect.x + face.x * imageRect.width,
  cy: imageRect.y + face.y * imageRect.height,
  r: face.r * imageRect.width,
});

/** Keeps the zoom in range and the photo within reach: its slack past the frame plus a free margin. */
export const clampAdjustment = (frame: Rect, image: Size, adjustment: PhotoAdjustment): PhotoAdjustment => {
  const scale = clamp(adjustment.scale, MIN_SCALE, MAX_SCALE);
  const rect = getImageRect(frame, image, { scale, offsetX: 0, offsetY: 0 });
  const maxOffsetX = Math.max(0, (rect.width - frame.width) / 2) / frame.width + FREE_PAN_MARGIN;
  const maxOffsetY = Math.max(0, (rect.height - frame.height) / 2) / frame.height + FREE_PAN_MARGIN;
  return {
    scale,
    offsetX: clamp(adjustment.offsetX, -maxOffsetX, maxOffsetX),
    offsetY: clamp(adjustment.offsetY, -maxOffsetY, maxOffsetY),
  };
};

export const clampFace = (face: FaceCover): FaceCover => ({
  x: clamp(face.x, 0, 1),
  y: clamp(face.y, 0, 1),
  r: clamp(face.r, MIN_FACE_RADIUS, MAX_FACE_RADIUS),
});

/** Moves a photo by a delta in card units. */
export const panAdjustment = (frame: Rect, image: Size, adjustment: PhotoAdjustment, delta: Point): PhotoAdjustment =>
  clampAdjustment(frame, image, {
    ...adjustment,
    offsetX: adjustment.offsetX + delta.x / frame.width,
    offsetY: adjustment.offsetY + delta.y / frame.height,
  });

export const zoomAdjustment = (
  frame: Rect,
  image: Size,
  adjustment: PhotoAdjustment,
  factor: number,
): PhotoAdjustment => clampAdjustment(frame, image, { ...adjustment, scale: adjustment.scale * factor });

/** Moves the face cover by a delta in card units (converted into image fractions). */
export const moveFace = (imageRect: Rect, face: FaceCover, delta: Point): FaceCover =>
  clampFace({ ...face, x: face.x + delta.x / imageRect.width, y: face.y + delta.y / imageRect.height });

export const resizeFace = (face: FaceCover, factor: number): FaceCover => clampFace({ ...face, r: face.r * factor });

const isInside = (point: Point, rect: Rect) =>
  point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;

const isInCircle = (point: Point, circle: Circle) => {
  const dx = point.x - circle.cx;
  const dy = point.y - circle.cy;
  return dx * dx + dy * dy <= circle.r * circle.r;
};

/** Which photo is visible at `point` in slider mode: left of the divider is "before". */
const sliderSlotAt = (point: Point, split: number): PhotoCompareSlotKey =>
  point.x < split * CARD_WIDTH ? "before" : "after";

/**
 * Resolves what a touch at `point` (card units) grabs. Face covers win over everything so they
 * stay draggable, then the slider divider, then the photo under the finger.
 */
export const hitTest = (
  state: PhotoCompareState,
  images: Record<PhotoCompareSlotKey, Size | null>,
  point: Point,
): HitTarget => {
  const frames = getFrames(state.mode);
  const photoArea = { x: 0, y: 0, width: CARD_WIDTH, height: PHOTO_AREA_HEIGHT };
  if (!isInside(point, photoArea)) {
    return null;
  }

  if (state.hideFaces) {
    // In slider mode only the visible half of each photo shows its cover.
    const candidates: PhotoCompareSlotKey[] =
      state.mode === "slider" ? [sliderSlotAt(point, state.split)] : ["before", "after"];
    for (const slot of candidates) {
      const image = images[slot];
      if (!image) {
        continue;
      }
      const circle = getFaceCircle(getImageRect(frames[slot], image, state[slot].adjustment), state[slot].face);
      const handle = getFaceHandle(circle);
      if (Math.hypot(point.x - handle.x, point.y - handle.y) <= FACE_HANDLE_HIT_SLOP) {
        return { kind: "faceHandle", slot };
      }
      if (isInCircle(point, circle)) {
        return { kind: "face", slot };
      }
    }
  }

  if (state.mode === "slider") {
    if (Math.abs(point.x - state.split * CARD_WIDTH) <= DIVIDER_HIT_SLOP) {
      return { kind: "divider" };
    }
    return { kind: "photo", slot: sliderSlotAt(point, state.split) };
  }

  if (isInside(point, frames.before)) {
    return { kind: "photo", slot: "before" };
  }
  if (isInside(point, frames.after)) {
    return { kind: "photo", slot: "after" };
  }
  return null;
};

export const setSplit = (state: PhotoCompareState, x: number): PhotoCompareState => ({
  ...state,
  split: clamp(x / CARD_WIDTH, 0.05, 0.95),
});

export type ImageSizes = Record<PhotoCompareSlotKey, Size | null>;

/** Radius (card units) a freshly placed face cover gets, whatever the photo's zoom. */
export const FACE_COVER_PLACE_RADIUS = 90;
/** Where a freshly placed cover sits: this far down the visible frame, above the torso. */
export const FACE_COVER_PLACE_Y = 0.18;

/**
 * Puts the slot's face cover inside the part of its frame the client can actually see (in slider
 * mode each photo is only visible on its side of the divider), so toggling the covers on never
 * produces an invisible disc that has scrolled out with a panned or shrunk photo. The cover is
 * still stored in image coordinates and keeps following the face afterwards.
 */
export const placeFaceCoverInView = (
  state: PhotoCompareState,
  sizes: ImageSizes,
  slot: PhotoCompareSlotKey,
): PhotoCompareState => {
  const image = sizes[slot];
  if (!image) {
    return state;
  }
  const frame = getFrames(state.mode)[slot];
  const visibleLeft = state.mode === "slider" && slot === "after" ? state.split * CARD_WIDTH : frame.x;
  const visibleRight = state.mode === "slider" && slot === "before" ? state.split * CARD_WIDTH : frame.x + frame.width;
  const point = { x: (visibleLeft + visibleRight) / 2, y: frame.y + frame.height * FACE_COVER_PLACE_Y };
  const rect = getImageRect(frame, image, state[slot].adjustment);
  const face = clampFace({
    x: (point.x - rect.x) / rect.width,
    y: (point.y - rect.y) / rect.height,
    r: FACE_COVER_PLACE_RADIUS / rect.width,
  });
  return { ...state, [slot]: { ...state[slot], face } };
};

export const placeFaceCoversInView = (state: PhotoCompareState, sizes: ImageSizes): PhotoCompareState =>
  placeFaceCoverInView(placeFaceCoverInView(state, sizes, "before"), sizes, "after");

/** Applies a one-finger drag (`delta` in card units) to whatever the gesture grabbed. */
export const applyPan = (
  state: PhotoCompareState,
  sizes: ImageSizes,
  target: HitTarget,
  delta: Point,
  point: Point,
): PhotoCompareState => {
  if (!target) {
    return state;
  }
  if (target.kind === "divider") {
    return setSplit(state, point.x);
  }
  const image = sizes[target.slot];
  if (!image) {
    return state;
  }
  const frame = getFrames(state.mode)[target.slot];
  const slot = state[target.slot];
  if (target.kind === "face") {
    const imageRect = getImageRect(frame, image, slot.adjustment);
    return { ...state, [target.slot]: { ...slot, face: moveFace(imageRect, slot.face, delta) } };
  }
  if (target.kind === "faceHandle") {
    return { ...state, [target.slot]: { ...slot, face: resizeFaceTo(frame, image, slot, point) } };
  }
  return { ...state, [target.slot]: { ...slot, adjustment: panAdjustment(frame, image, slot.adjustment, delta) } };
};

/** Sets the cover's radius so its edge passes through `point` (card units) — the grip follows the finger. */
export const resizeFaceTo = (frame: Rect, image: Size, slot: PhotoCompareSlot, point: Point): FaceCover => {
  const imageRect = getImageRect(frame, image, slot.adjustment);
  const circle = getFaceCircle(imageRect, slot.face);
  const radius = Math.hypot(point.x - circle.cx, point.y - circle.cy);
  return clampFace({ ...slot.face, r: radius / imageRect.width });
};

/** Applies a pinch / wheel zoom `factor` to whatever the gesture grabbed. */
export const applyZoom = (
  state: PhotoCompareState,
  sizes: ImageSizes,
  target: HitTarget,
  factor: number,
): PhotoCompareState => {
  if (!target || target.kind === "divider") {
    return state;
  }
  const image = sizes[target.slot];
  if (!image) {
    return state;
  }
  const slot = state[target.slot];
  if (target.kind === "face" || target.kind === "faceHandle") {
    return { ...state, [target.slot]: { ...slot, face: resizeFace(slot.face, factor) } };
  }
  const frame = getFrames(state.mode)[target.slot];
  return { ...state, [target.slot]: { ...slot, adjustment: zoomAdjustment(frame, image, slot.adjustment, factor) } };
};

/** The slider's "before" half: everything left of the divider. */
export const getSliderClipRect = (split: number): Rect => ({
  x: 0,
  y: 0,
  width: split * CARD_WIDTH,
  height: PHOTO_AREA_HEIGHT,
});
